import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Store, Upload, Download, Users, Shield, MapPin, Bell, Monitor, FileText, Trash2, Edit2, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import PermissionEditor from '@/components/PermissionEditor';

const Settings = () => {
  const { user } = useContext(AuthContext);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [countAreas, setCountAreas] = useState([]);
  const [settingsPermissions, setSettingsPermissions] = useState({
    stores: ['store_manager', 'complex_manager', 'regional_manager', 'network_admin'],
    count_areas: ['store_manager', 'complex_manager', 'regional_manager', 'network_admin'],
    team_directory: ['store_manager', 'complex_manager', 'regional_manager', 'network_admin'],
    permissions: ['network_admin'],
    import_export: ['network_admin'],
    display: ['regional_manager', 'network_admin']
  });
  const [settingsAccessExpanded, setSettingsAccessExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  const [storeFormData, setStoreFormData] = useState({
    name: '',
    code: '',
    location: ''
  });
  const [userFormData, setUserFormData] = useState({
    team_member_id: '',
    name: '',
    email: '',
    role: 'none',
    store_id: '',
    password: 'password'
  });
  const [areaFormData, setAreaFormData] = useState({
    name: '',
    description: '',
    day: 'Sunday',
    sort_order: 0
  });
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
    fetchSettingsPermissions();
  }, []);

  const fetchData = async () => {
    try {
      const [storesRes, usersRes, areasRes] = await Promise.all([
        fetch(`${API}/stores`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/count-areas`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStores(await storesRes.json());
      setUsers(await usersRes.json());
      if (areasRes.ok) {
        setCountAreas(await areasRes.json());
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSettingsPermissions = async () => {
    try {
      const response = await fetch(`${API}/settings-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettingsPermissions(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings permissions:', error);
    }
  };

  const updateSettingsPermissions = async (newPermissions) => {
    try {
      const response = await fetch(`${API}/settings-permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPermissions)
      });
      if (response.ok) {
        setSettingsPermissions(newPermissions);
        toast.success('Settings permissions updated');
      } else {
        toast.error('Failed to update permissions');
      }
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  // Check if user can access a specific settings tab
  const canAccessTab = (tabKey) => {
    // Network admin always has access to everything
    if (user?.role === 'network_admin') return true;
    
    const allowedRoles = settingsPermissions[tabKey] || [];
    return allowedRoles.includes(user?.role);
  };

  const handleStoreSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/stores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(storeFormData)
      });
      if (response.ok) {
        toast.success('Store added successfully');
        setOpen(false);
        setStoreFormData({ name: '', code: '', location: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add store');
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: userFormData.name,
          email: userFormData.email,
          role: userFormData.role,
          store_id: userFormData.store_id
        };
        
        // Only Network Admin can update team_member_id
        if (user?.role === 'network_admin' && userFormData.team_member_id !== editingUser.team_member_id) {
          updateData.team_member_id = userFormData.team_member_id;
        }
        
        const response = await fetch(`${API}/users/${editingUser.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        });
        if (response.ok) {
          toast.success('User updated successfully');
          setUserDialogOpen(false);
          setEditingUser(null);
          setUserFormData({ team_member_id: '', name: '', email: '', role: 'none', store_id: '', password: 'password' });
          fetchData();
        } else {
          const errorData = await response.json();
          toast.error(errorData.detail || 'Failed to update user');
        }
      } else {
        // Create new user
        const response = await fetch(`${API}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(userFormData)
        });
        if (response.ok) {
          toast.success('User added successfully');
          setUserDialogOpen(false);
          setUserFormData({ team_member_id: '', name: '', email: '', role: 'none', store_id: '', password: 'password' });
          fetchData();
        } else {
          const errorData = await response.json();
          toast.error(errorData.detail || 'Failed to add user');
        }
      }
    } catch (error) {
      toast.error(editingUser ? 'Failed to update user' : 'Failed to add user');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setUserFormData({
      team_member_id: user.team_member_id,
      name: user.name,
      email: user.email || '',
      role: user.role,
      store_id: user.store_id || '',
      password: 'password'
    });
    setUserDialogOpen(true);
  };

  const closeUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
    setUserFormData({ team_member_id: '', name: '', email: '', role: 'none', store_id: '', password: 'password' });
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success(`${userName} deleted successfully`);
        fetchData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const canDeleteUsers = user?.role === 'network_admin' || user?.role === 'regional_manager';
  const canDeleteStores = user?.role === 'network_admin';

  // Upload store map function
  const handleMapUpload = async (storeId, file) => {
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API}/stores/${storeId}/map`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Store map uploaded successfully');
        fetchData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to upload map');
      }
    } catch (error) {
      toast.error('Failed to upload map');
    }
  };

  // Delete store function
  const deleteStore = async (storeId, storeName) => {
    if (!window.confirm(`Are you sure you want to delete "${storeName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/stores/${storeId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success(`${storeName} deleted successfully`);
        fetchData();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to delete store');
      }
    } catch (error) {
      toast.error('Failed to delete store');
    }
  };

  // Check if user can manage count areas
  const canManageCountAreas = ['network_admin', 'regional_manager', 'complex_manager', 'store_manager'].includes(user?.role);

  // Count Areas functions
  const handleAreaSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingArea 
        ? `${API}/count-areas/${editingArea.id}`
        : `${API}/count-areas`;
      
      const response = await fetch(url, {
        method: editingArea ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(areaFormData)
      });

      if (response.ok) {
        toast.success(editingArea ? 'Area updated successfully' : 'Area added successfully');
        setAreaDialogOpen(false);
        setEditingArea(null);
        setAreaFormData({ name: '', description: '', day: 'Sunday', sort_order: 0 });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save area');
      }
    } catch (error) {
      toast.error('Failed to save area');
    }
  };

  const openEditAreaDialog = (area) => {
    setEditingArea(area);
    setAreaFormData({
      name: area.name,
      description: area.description || '',
      day: area.day,
      sort_order: area.sort_order || 0
    });
    setAreaDialogOpen(true);
  };

  const deleteArea = async (areaId, areaName) => {
    if (!window.confirm(`Are you sure you want to delete "${areaName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API}/count-areas/${areaId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`"${areaName}" deleted successfully`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete area');
      }
    } catch (error) {
      toast.error('Failed to delete area');
    }
  };

  const closeAreaDialog = () => {
    setAreaDialogOpen(false);
    setEditingArea(null);
    setAreaFormData({ name: '', description: '', day: 'Sunday', sort_order: 0 });
  };

  const getAreasByDay = (day) => {
    return countAreas
      .filter(a => a.day === day)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  };

  const dayColors = {
    Sunday: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
    Monday: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
    Tuesday: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' }
  };

  const countDays = ['Sunday', 'Monday', 'Tuesday'];

  const roles = [
    { key: 'none', name: 'None (Basic Access)', description: 'Can view roster and check-in' },
    { key: 'stocktake_champion', name: 'Stocktake Champion', description: 'Assists controller, manages equipment' },
    { key: 'stocktake_controller', name: 'Stocktake Controller', description: 'Manages stocktake planning' },
    { key: 'stocktake_auditor', name: 'Stocktake Auditor', description: 'Audit and verification access' },
    { key: 'supervisor', name: 'Supervisor', description: 'Oversees operations and verifications' },
    { key: 'store_manager', name: 'Coordinator', description: 'Full store access' },
    { key: 'complex_manager', name: 'Complex Manager', description: 'Multi-store oversight' },
    { key: 'regional_manager', name: 'Regional Manager', description: 'All stores in region' },
    { key: 'network_admin', name: 'Network Admin', description: 'Full system access' }
  ];

  const isNetworkAdmin = user?.role === 'network_admin';

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Settings</h1>
        <p className="text-slate-600">Configure stores, groups, and team directory</p>
      </div>

      <Tabs defaultValue={canAccessTab('stores') ? 'stores' : (canAccessTab('count_areas') ? 'count-areas' : 'team')} className="w-full">
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
          {canAccessTab('stores') && (
            <TabsTrigger value="stores" className="gap-2">
              <Store className="w-4 h-4" />
              <span className="hidden sm:inline">Stores</span>
            </TabsTrigger>
          )}
          {canAccessTab('count_areas') && (
            <TabsTrigger value="count-areas" className="gap-2">
              <MapPin className="w-4 h-4" />
              <span className="hidden sm:inline">Count Areas</span>
            </TabsTrigger>
          )}
          {canAccessTab('team_directory') && (
            <TabsTrigger value="team" className="gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Team Directory</span>
            </TabsTrigger>
          )}
          {canAccessTab('permissions') && (
            <TabsTrigger value="permissions" className="gap-2">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Permissions</span>
            </TabsTrigger>
          )}
          {canAccessTab('import_export') && (
            <TabsTrigger value="import" className="gap-2">
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Import/Export</span>
            </TabsTrigger>
          )}
          {canAccessTab('display') && (
            <TabsTrigger value="display" className="gap-2">
              <Monitor className="w-4 h-4" />
              <span className="hidden sm:inline">Display</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Stores Tab */}
        {canAccessTab('stores') && (
          <TabsContent value="stores" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-900">Stores</h2>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-teal-700 hover:bg-teal-800" data-testid="add-store-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Store
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-store-dialog">
                <DialogHeader>
                  <DialogTitle>Add New Store</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleStoreSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Store Name</Label>
                    <Input
                      id="name"
                      value={storeFormData.name}
                      onChange={(e) => setStoreFormData({ ...storeFormData, name: e.target.value })}
                      placeholder="Indooroopilly SFS"
                      required
                      data-testid="store-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="code">Store Code</Label>
                    <Input
                      id="code"
                      value={storeFormData.code}
                      onChange={(e) => setStoreFormData({ ...storeFormData, code: e.target.value })}
                      placeholder="#8197"
                      required
                      data-testid="store-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={storeFormData.location}
                      onChange={(e) => setStoreFormData({ ...storeFormData, location: e.target.value })}
                      placeholder="Indooroopilly, QLD"
                      required
                      data-testid="store-location-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-teal-700 hover:bg-teal-800" data-testid="submit-store-button">
                    Add Store
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {stores.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Store className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">No stores configured yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stores.map((store) => (
                <div
                  key={store.id}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
                  data-testid={`store-card-${store.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-teal-100 text-teal-700">
                      <Store className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full font-semibold">
                        {store.code || store.store_number || '-'}
                      </span>
                      {canDeleteStores && (
                        <button
                          onClick={() => deleteStore(store.id, store.name)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete store"
                          data-testid={`delete-store-${store.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{store.name}</h3>
                  <p className="text-sm text-slate-600 mb-3">{store.location || store.region || '-'}</p>
                  
                  {/* Store Map Status */}
                  {user?.role === 'network_admin' && (
                    <div className="pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-600">
                            {store.map_image_url ? 'Map uploaded' : 'No map'}
                          </span>
                          {store.map_image_url && (
                            <span className="w-2 h-2 bg-green-500 rounded-full" />
                          )}
                        </div>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => handleMapUpload(store.id, e.target.files[0])}
                            data-testid={`map-upload-${store.id}`}
                          />
                          <span className="text-xs px-2 py-1 bg-teal-50 text-teal-700 rounded hover:bg-teal-100 transition-colors">
                            {store.map_image_url ? 'Replace' : 'Upload Map'}
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </TabsContent>
        )}

        {/* Count Areas Tab */}
        {canAccessTab('count_areas') && (
          <TabsContent value="count-areas" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
                  <MapPin className="w-6 h-6" />
                  Count Areas by Day
                </h2>
                <p className="text-sm text-slate-500 mt-1">Define which areas are counted on Sunday, Monday, and Tuesday</p>
              </div>
              <Dialog open={areaDialogOpen} onOpenChange={(open) => open ? setAreaDialogOpen(true) : closeAreaDialog()}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-700 hover:bg-teal-800" data-testid="add-area-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Area
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-area-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold">
                      {editingArea ? 'EDIT COUNT AREA' : 'ADD COUNT AREA'}
                    </DialogTitle>
                    <p className="text-sm text-slate-500">Define an area to be counted on a specific day</p>
                  </DialogHeader>
                  <form onSubmit={handleAreaSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="area_name">Area Name *</Label>
                      <Input
                        id="area_name"
                        value={areaFormData.name}
                        onChange={(e) => setAreaFormData({ ...areaFormData, name: e.target.value })}
                        placeholder="e.g., Nursery, Timber Yard, Landscape"
                        required
                        className="mt-1 border-teal-500 focus:ring-teal-500"
                        data-testid="area-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="area_description">Description (optional)</Label>
                      <Input
                        id="area_description"
                        value={areaFormData.description}
                        onChange={(e) => setAreaFormData({ ...areaFormData, description: e.target.value })}
                        placeholder="Additional details about this area"
                        className="mt-1"
                        data-testid="area-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Count Day *</Label>
                        <Select 
                          value={areaFormData.day} 
                          onValueChange={(val) => setAreaFormData({ ...areaFormData, day: val })}
                        >
                          <SelectTrigger className="mt-1" data-testid="area-day-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {countDays.map(day => (
                              <SelectItem key={day} value={day}>{day}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="sort_order">Sort Order</Label>
                        <Input
                          id="sort_order"
                          type="number"
                          value={areaFormData.sort_order}
                          onChange={(e) => setAreaFormData({ ...areaFormData, sort_order: parseInt(e.target.value) || 0 })}
                          className="mt-1"
                          data-testid="area-sort-input"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={closeAreaDialog}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-teal-700 hover:bg-teal-800" data-testid="submit-area-button">
                        {editingArea ? 'Save Changes' : 'Add Area'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Areas grouped by day */}
            <div className="space-y-6">
              {countDays.map(day => {
                const dayAreas = getAreasByDay(day);
                const colors = dayColors[day];
                
                return (
                  <div key={day} className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Calendar className={`w-5 h-5 ${colors.text}`} />
                      <h3 className={`text-lg font-bold ${colors.text}`}>{day}</h3>
                      <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                        {dayAreas.length} area{dayAreas.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    {dayAreas.length === 0 ? (
                      <div className={`p-4 rounded-lg border ${colors.border} ${colors.bg} text-center`}>
                        <p className="text-sm text-slate-500">No areas defined for {day}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {dayAreas.map(area => (
                          <div 
                            key={area.id}
                            className={`flex items-center justify-between p-4 rounded-lg border ${colors.border} ${colors.bg}`}
                            data-testid={`count-area-${area.id}`}
                          >
                            <div>
                              <h4 className="font-medium text-slate-900">{area.name}</h4>
                              {area.description && (
                                <p className="text-sm text-slate-500 mt-0.5">{area.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditAreaDialog(area)}
                                className="h-8 w-8 p-0"
                                data-testid={`edit-area-${area.id}`}
                              >
                                <Edit2 className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteArea(area.id, area.name)}
                                className="h-8 w-8 p-0"
                                data-testid={`delete-area-${area.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-slate-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        )}

        {/* Team Directory Tab */}
        {canAccessTab('team_directory') && (
          <TabsContent value="team" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-slate-900">Team Directory</h2>
              <Dialog open={userDialogOpen} onOpenChange={(open) => open ? setUserDialogOpen(true) : closeUserDialog()}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-700 hover:bg-teal-800" data-testid="add-user-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-user-dialog">
                  <DialogHeader>
                    <DialogTitle>{editingUser ? 'Edit Team Member' : 'Add New Team Member'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUserSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="team_member_id">Team Member ID (Login Number)</Label>
                      <Input
                        id="team_member_id"
                        value={userFormData.team_member_id}
                        onChange={(e) => setUserFormData({ ...userFormData, team_member_id: e.target.value })}
                        placeholder="424007"
                        required
                        maxLength={6}
                        disabled={editingUser && user?.role !== 'network_admin'}
                        data-testid="user-id-input"
                      />
                      {editingUser && user?.role !== 'network_admin' && (
                        <p className="text-xs text-slate-500 mt-1">Team Member ID cannot be changed</p>
                      )}
                      {editingUser && user?.role === 'network_admin' && (
                        <p className="text-xs text-amber-600 mt-1">Network Admin: You can change the Team Member ID (login number)</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={userFormData.name}
                        onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                        placeholder="John Smith"
                        required
                        data-testid="user-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email (Optional)</Label>
                      <Input
                        id="email"
                        type="email"
                        value={userFormData.email}
                        onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                        placeholder="john@indooroopilly.com"
                        data-testid="user-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="role">Role</Label>
                      <Select value={userFormData.role || "none"} onValueChange={(val) => setUserFormData({ ...userFormData, role: val })}>
                        <SelectTrigger data-testid="user-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.key} value={role.key}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="store">Assigned Store</Label>
                      <Select value={userFormData.store_id || "none"} onValueChange={(val) => setUserFormData({ ...userFormData, store_id: val === "none" ? "" : val })}>
                        <SelectTrigger data-testid="user-store-select">
                          <SelectValue placeholder="Select store (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No specific store</SelectItem>
                          {stores.map((store) => (
                            <SelectItem key={store.id} value={store.id}>
                              {store.name} ({store.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        Users assigned to a store will only see that store's data
                      </p>
                    </div>
                    <Button type="submit" className="w-full bg-teal-700 hover:bg-teal-800" data-testid="submit-user-button">
                      {editingUser ? 'Update User' : 'Add User'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">Team Member ID</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">Name</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">Store</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">Role</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {users.map((u) => {
                    const userStore = stores.find(s => s.id === u.store_id);
                    return (
                      <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                        <td className="px-6 py-4 font-mono text-sm">{u.team_member_id}</td>
                        <td className="px-6 py-4 font-medium text-slate-900">{u.name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {userStore ? `${userStore.name} (${userStore.code})` : 'All Stores'}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs px-2.5 py-1 bg-teal-100 text-teal-800 rounded-full font-semibold">
                            {roles.find(r => r.key === u.role)?.name || u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{u.email || '—'}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(u)}
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                              data-testid={`edit-user-${u.id}`}
                            >
                              Edit
                            </Button>
                            {canDeleteUsers && u.id !== user?.id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteUser(u.id, u.name)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {/* Permissions Tab */}
        {canAccessTab('permissions') && (
          <TabsContent value="permissions" className="space-y-6">
            {/* Permission Reference */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-5 h-5 text-slate-700" />
                <h2 className="text-xl font-semibold text-slate-900">Permission Levels Reference</h2>
              </div>
              <p className="text-sm text-slate-600 mb-6">Use these values in the stocktake_role column</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {roles.map((role) => (
                  <div
                    key={role.key}
                    className="border border-slate-200 rounded-lg p-4 hover:border-teal-300 transition-colors"
                    data-testid={`role-card-${role.key}`}
                  >
                    <div className="font-mono text-xs text-teal-700 bg-teal-50 px-2 py-1 rounded mb-2 inline-block">
                      {role.key}
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{role.name}</h3>
                    <p className="text-xs text-slate-600">{role.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Customize Page Access - moved above Settings Page Access */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-5 h-5 text-slate-700" />
                <h2 className="text-xl font-semibold text-slate-900">Customize Page Access</h2>
              </div>
              <PermissionEditor />
            </div>

            {/* Settings Tab Access - Collapsible */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => setSettingsAccessExpanded(!settingsAccessExpanded)}
                className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
                data-testid="settings-access-toggle"
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-slate-700" />
                  <h2 className="text-xl font-semibold text-slate-900">Settings Page Access</h2>
                </div>
                {settingsAccessExpanded ? (
                  <ChevronUp className="w-5 h-5 text-slate-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-500" />
                )}
              </button>
              
              {settingsAccessExpanded && (
                <div className="px-6 pb-6 border-t border-slate-100">
                  <p className="text-sm text-slate-600 my-4">
                    Control which roles can access each tab in the Settings page
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 font-semibold text-slate-700">Settings Tab</th>
                          {roles.filter(r => r.key !== 'none' && r.key !== 'network_admin').map((role) => {
                            // Custom display names for specific roles
                            const displayNames = {
                              'store_manager': 'Coordinator',
                              'complex_manager': 'Complex Manager',
                              'regional_manager': 'Regional Manager'
                            };
                            const displayName = displayNames[role.key] || role.name.split(' ').pop();
                            return (
                              <th key={role.key} className="text-center py-3 px-2 font-semibold text-slate-700">
                                <span className="text-xs">{displayName}</span>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'stores', label: 'Stores' },
                          { key: 'count_areas', label: 'Count Areas' },
                          { key: 'team_directory', label: 'Team Directory' },
                          { key: 'permissions', label: 'Permissions' },
                          { key: 'import_export', label: 'Import/Export' },
                          { key: 'display', label: 'Display' }
                        ].map((tab) => (
                          <tr key={tab.key} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium text-slate-900">{tab.label}</td>
                            {roles.filter(r => r.key !== 'none' && r.key !== 'network_admin').map((role) => (
                              <td key={role.key} className="text-center py-3 px-2">
                                <input
                                  type="checkbox"
                                  checked={settingsPermissions[tab.key]?.includes(role.key) || false}
                                  onChange={(e) => {
                                    const newPermissions = { ...settingsPermissions };
                                    if (e.target.checked) {
                                      newPermissions[tab.key] = [...(newPermissions[tab.key] || []), role.key];
                                    } else {
                                      newPermissions[tab.key] = (newPermissions[tab.key] || []).filter(r => r !== role.key);
                                    }
                                    updateSettingsPermissions(newPermissions);
                                  }}
                                  className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                                  data-testid={`settings-perm-${tab.key}-${role.key}`}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 mt-4">
                    Note: Network Admin always has access to all tabs.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* Import/Export Tab */}
        {canAccessTab('import_export') && (
          <TabsContent value="import" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Import Team Directory */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Upload className="w-5 h-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Import Team Directory</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">Upload team members from CSV or Excel file</p>
                
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Required columns: *</p>
                  <p className="text-xs font-mono text-red-600">
                    team_member_id, name, store_number, role, stocktake_role, department, phone, email, is_active
                  </p>
                  <p className="text-xs text-slate-600 mt-2">
                    Only Team ID, Name and Store are required. Other fields can be added later.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="w-4 h-4" />
                    Download Template
                  </Button>
                  <Button size="sm" className="gap-2 bg-teal-600 hover:bg-teal-700">
                    <Upload className="w-4 h-4" />
                    Upload CSV/Excel
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2">
                    <FileText className="w-4 h-4" />
                    Upload Screenshot
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => setUserDialogOpen(true)}>
                    <Plus className="w-4 h-4" />
                    Add Manually
                  </Button>
                </div>
              </div>

              {/* Import Stores */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="w-5 h-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Import Stores</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">Upload stores from CSV or Excel file</p>
                
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Required columns: *</p>
                  <p className="text-xs font-mono text-red-600">
                    name, store_number, manager_name, stocktake_controller, stocktake_champion, auditor, address, phone
                  </p>
                  <p className="text-xs text-slate-600 mt-2">
                    Only Name and Store Number are required. Other fields can be added later.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="w-4 h-4" />
                    Download Template
                  </Button>
                  <Button size="sm" className="gap-2 bg-teal-600 hover:bg-teal-700">
                    <Upload className="w-4 h-4" />
                    Upload CSV/Excel
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2">
                    <FileText className="w-4 h-4" />
                    Upload Screenshot
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Display Tab */}
        {canAccessTab('display') && (
          <TabsContent value="display" className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Monitor className="w-5 h-5 text-slate-700" />
                <h3 className="text-lg font-semibold text-slate-900">Display Settings</h3>
              </div>
              <p className="text-sm text-slate-600 mb-6">Configure display preferences and notifications</p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Countdown Timer</p>
                    <p className="text-sm text-slate-600">Show countdown on dashboard</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 text-teal-600 rounded" />
                </div>
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Progress Tracking</p>
                    <p className="text-sm text-slate-600">Show progress bars on checklists</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 text-teal-600 rounded" />
                </div>
                <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-600">Send email updates for stocktake events</p>
                  </div>
                  <input type="checkbox" className="w-5 h-5 text-teal-600 rounded" />
                </div>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
